import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trash2, Truck, Package, Leaf, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Trash2: Trash2,
  Truck: Truck,
  Package: Package,
  Leaf: Leaf
};

const serviceImages = {
  'junk-removal': 'https://images.unsplash.com/photo-1584786454449-ecc77738684c?w=800&q=80',
  'deliveries': 'https://images.unsplash.com/photo-1616432043562-3671ea2e5242?w=800&q=80',
  'moving': 'https://images.unsplash.com/photo-1601467995997-ac1ae9a8fff4?w=800&q=80'
};

const Services = ({ onBookNow }) => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16" data-testid="services-header">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Our <span className="gold-gradient">Services</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Comprehensive delivery and hauling solutions for residential and commercial clients across Calgary
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Truck;
            return (
              <div 
                key={service.id}
                className="bg-[#1a1a1a] rounded-xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300 group"
                data-testid={`service-card-${service.id}`}
              >
                <Link to={`/services/${service.id}`}>
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={serviceImages[service.id]} 
                      alt={service.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <IconComponent className="w-12 h-12 text-[#d4af37]" />
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-2xl font-bold mb-3 text-[#d4af37] group-hover:text-[#b8941f] transition">{service.name}</h3>
                    <p className="text-gray-400 mb-4">{service.description}</p>
                    
                    <ul className="space-y-2 mb-6">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-300 text-sm">
                          <ArrowRight className="w-4 h-4 text-[#d4af37] mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                      {service.features.length > 3 && (
                        <li className="text-[#d4af37] text-sm font-semibold">
                          + {service.features.length - 3} more features
                        </li>
                      )}
                    </ul>
                    
                    <div className="flex gap-2">
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          onBookNow(service.name);
                        }}
                        className="flex-1 bg-[#d4af37] text-black px-6 py-3 rounded-lg font-semibold hover:bg-[#b8941f] transition"
                        data-testid={`book-${service.id}`}
                      >
                        Book Now
                      </button>
                      <Link
                        to={`/services/${service.id}`}
                        className="flex-1 bg-transparent border-2 border-[#d4af37] text-[#d4af37] px-6 py-3 rounded-lg font-semibold hover:bg-[#d4af37] hover:text-black transition text-center"
                      >
                        Learn More
                      </Link>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;